import {
  AdminDatePicker,
  CreateButton,
  EditButton,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
  adminEndOfDay,
  message,
  toAdminEndOfDayISOString,
} from '@wanjianshi/admin-ui';
import type { ApiResponse } from '@wanjianshi/shared-contracts';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '@/auth/store';
import { AdminRemoteSelect } from '@/components/AdminRemoteSelect';
import { adminApi, adminOperationOptions } from '@/services/api/client';

type MembershipKind = 'personal' | 'team';
type MembershipMode = 'create' | 'edit';
type MembershipRow = Record<string, unknown>;

interface MembershipPlanOption {
  id: string | number;
  name: string;
  type?: number | null;
  type_name?: string | null;
  package_days?: number | null;
  default_days: number;
  billing_cycle: 'monthly' | 'quarterly' | 'yearly';
  max_members?: number | null;
}

interface UserSubjectOption {
  id: string | number;
  username?: string | null;
  nickname?: string | null;
  mobile?: string | null;
}

interface TeamSubjectOption {
  id: string | number;
  name: string;
  founder_username?: string | null;
  founder_nickname?: string | null;
  founder_mobile?: string | null;
}

interface SelectOption<T extends string | number = string | number> {
  label: string;
  value: T;
}

const cycleLabels: Record<MembershipPlanOption['billing_cycle'], string> = {
  monthly: '月度',
  quarterly: '季度',
  yearly: '年度',
};

function subjectLabel(kind: MembershipKind, row: UserSubjectOption | TeamSubjectOption): string {
  if (kind === 'personal') {
    const user = row as UserSubjectOption;
    return `${user.mobile || '无手机号'} · ${user.nickname || user.username || `用户 ${user.id}`}`;
  }
  const team = row as TeamSubjectOption;
  return `${team.name} · ${team.founder_mobile || '无手机号'} · ${team.founder_nickname || team.founder_username || '未知所属人'}`;
}

function initialSubjectOption(kind: MembershipKind, row?: MembershipRow): SelectOption<string> | null {
  if (!row) return null;
  const value = String(row.subject_id ?? '').trim();
  if (!value) return null;
  if (kind === 'personal') {
    return {
      value,
      label: `${String(row.mobile_masked || '手机号未提供')} · ${String(row.subject_name || row.username || `用户 ${value}`)}`,
    };
  }
  return {
    value,
    label: `${String(row.subject_name || `团队 ${value}`)} · ${String(row.founder_mobile_masked || '手机号未提供')} · ${String(row.founder_nickname || row.founder_username || '未知所属人')}`,
  };
}

export function MembershipEditorModal({
  kind,
  mode,
  row,
  onSaved,
}: {
  kind: MembershipKind;
  mode: MembershipMode;
  row?: MembershipRow;
  onSaved: () => Promise<void>;
}) {
  const opensTeamMembership = kind === 'team' && mode === 'edit' && !row?.subscription_id;
  const submitAsCreate = mode === 'create' || opensTeamMembership;
  const permission = `wjs-admin:${kind === 'personal' ? 'personal' : 'team'}-membership:${submitAsCreate ? 'create' : 'update'}`;
  const allowed = useAdminAuth(state => state.hasPermission(permission));
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [plans, setPlans] = useState<MembershipPlanOption[]>([]);
  const [form] = Form.useForm();
  const watchedPlanId = Form.useWatch('plan_id', form);

  const subjectName = kind === 'personal' ? '账户' : '团队';
  const title = `${mode === 'create' ? '新增' : '编辑'}${kind === 'personal' ? '个人会员' : '团队会员'}`;
  const selectedSubject = useMemo(() => initialSubjectOption(kind, row), [kind, row]);
  const currentTeamMemberCount = kind === 'team' ? Math.max(1, Number(row?.member_count || 1)) : 1;
  const selectedPlan = plans.find(plan => String(plan.id) === String(watchedPlanId || ''));
  const configuredMaxSeats = Number(selectedPlan?.max_members ?? 0);

  const loadPlans = useCallback(async () => {
    setPlanLoading(true);
    try {
      const response = await adminApi.get<ApiResponse<{ items: MembershipPlanOption[] }>>(
        '/admin/membership-options/plans',
        { params: { scope: kind } },
      );
      setPlans(response.data.items ?? []);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '会员套餐加载失败');
    } finally {
      setPlanLoading(false);
    }
  }, [kind]);

  const loadSubjects = useCallback(async (keyword: string, signal: AbortSignal) => {
    const endpoint = kind === 'personal'
      ? '/admin/membership-options/users'
      : '/admin/membership-options/teams';
    const response = await adminApi.get<
      ApiResponse<{ items: Array<UserSubjectOption | TeamSubjectOption> }>
    >(endpoint, { params: { keyword, limit: 20 }, signal });
    return response.data.items ?? [];
  }, [kind]);

  useEffect(() => {
    if (!open) return;
    const initialSubject = initialSubjectOption(kind, row);
    form.resetFields();
    const existingSeats = Number(row?.seat_count || 0);
    form.setFieldsValue({
      subject_id: initialSubject?.value,
      plan_id: row?.plan_id ? String(row.plan_id) : undefined,
      seat_count: kind === 'team'
        ? Math.max(currentTeamMemberCount, existingSeats || 2)
        : undefined,
      expires_at: row?.expires_at ? adminEndOfDay(dayjs(String(row.expires_at))) : undefined,
    });
    void loadPlans();
  }, [currentTeamMemberCount, form, kind, loadPlans, open, row]);

  if (!allowed) return null;

  const planOptions: SelectOption<string>[] = plans.map(plan => ({
    value: String(plan.id),
    label: `${plan.name} · ${cycleLabels[plan.billing_cycle]} · ${plan.default_days} 天`,
  }));
  if (row?.plan_id && !planOptions.some(option => option.value === String(row.plan_id))) {
    planOptions.unshift({
      value: String(row.plan_id),
      label: `${String(row.plan_name || `套餐 ${String(row.plan_id)}`)} · 当前套餐`,
    });
  }

  const submit = async () => {
    const values = await form.validateFields();
    const reason = String(values.reason || '').trim();
    const expiresAt = values.expires_at as Dayjs;
    const body: Record<string, unknown> = {
      plan_id: String(values.plan_id),
      expires_at: toAdminEndOfDayISOString(expiresAt),
    };
    if (submitAsCreate) {
      body[kind === 'personal' ? 'user_id' : 'team_id'] = String(values.subject_id);
    }
    if (kind === 'team') body.member_count = Number(values.seat_count);
    setSubmitting(true);
    try {
      if (submitAsCreate) {
        await adminApi.post(
          kind === 'personal' ? '/admin/personal-memberships' : '/admin/team-memberships',
          body,
          adminOperationOptions(reason),
        );
      } else {
        const resourceId = String(kind === 'personal' ? row?.id ?? '' : row?.subject_id ?? '').trim();
        if (!resourceId) {
          message.error(`${subjectName}会员标识不存在，请刷新列表后重试`);
          return;
        }
        await adminApi.put(
          kind === 'personal'
            ? `/admin/personal-memberships/${resourceId}`
            : `/admin/team-memberships/${resourceId}`,
          body,
          adminOperationOptions(reason),
        );
      }
      message.success(`${title}成功`);
      setOpen(false);
      form.resetFields();
      await onSaved();
    } catch (error) {
      message.error(error instanceof Error ? error.message : `${title}失败`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {mode === 'create' ? (
        <CreateButton onClick={() => setOpen(true)}>{title}</CreateButton>
      ) : (
        <EditButton size="small" onClick={() => setOpen(true)}>编辑会员</EditButton>
      )}
      <Modal
        open={open}
        title={title}
        width={620}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
        onOk={() => void submit()}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" className="membership-editor-form">
          <Form.Item
            name="subject_id"
            label={subjectName}
            rules={[{ required: true, message: `请选择${subjectName}` }]}
            extra={mode === 'edit'
              ? opensTeamMembership
                ? '该团队当前未开通会员，保存后会创建团队会员记录'
                : `${subjectName}与现有会员记录绑定，编辑时不可更换`
              : undefined}
          >
            <AdminRemoteSelect<UserSubjectOption | TeamSubjectOption>
              allowClear={mode === 'create'}
              disabled={mode === 'edit'}
              loadOptions={loadSubjects}
              mapOption={item => ({
                value: String(item.id),
                label: subjectLabel(kind, item),
              })}
              initialOption={selectedSubject}
              placeholder={kind === 'personal'
                ? '输入用户名或手机号搜索，显示手机号与账户昵称'
                : '输入团队名称或所属人搜索，显示团队及所属人信息'}
            />
          </Form.Item>
          <Form.Item name="plan_id" label="会员套餐" rules={[{ required: true, message: '请选择会员套餐' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={planLoading}
              options={planOptions}
              placeholder={`选择${kind === 'personal' ? '个人' : '团队'}会员套餐`}
              onChange={planId => {
                const plan = plans.find(item => String(item.id) === String(planId));
                if (plan) form.setFieldValue('expires_at', adminEndOfDay(dayjs().add(plan.default_days, 'day')));
              }}
            />
          </Form.Item>
          {kind === 'team' ? (
            <Form.Item
              name="seat_count"
              label="购买席位数"
              rules={[
                { required: true, message: '请输入购买席位数' },
                {
                  validator: (_, value: number | undefined) => {
                    if (value === undefined || value === null) return Promise.resolve();
                    if (value < currentTeamMemberCount) {
                      return Promise.reject(new Error(`席位数不能少于当前成员数 ${currentTeamMemberCount} 人`));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra={configuredMaxSeats === -1
                ? `当前已有 ${currentTeamMemberCount} 名成员，套餐席位不限；后台可直接设置实际购买席位数`
                : configuredMaxSeats > 0
                  ? `当前已有 ${currentTeamMemberCount} 名成员，套餐配置上限 ${configuredMaxSeats} 个；后台设置可超过该上限`
                  : `当前已有 ${currentTeamMemberCount} 名成员；后台席位数可按实际需要设置`}
            >
              <InputNumber
                min={currentTeamMemberCount}
                max={10000}
                precision={0}
                style={{ width: '100%' }}
                placeholder="默认 2 个席位"
              />
            </Form.Item>
          ) : null}
          <Form.Item
            name="expires_at"
            label="套餐到期时间"
            rules={[{ required: true, message: '请选择套餐到期时间' }]}
            extra="选择套餐后会按套餐类型和有效天数自动计算，仍可手动修改"
          >
            <AdminDatePicker
              style={{ width: '100%' }}
              disabledDate={current => current.isBefore(dayjs().startOf('day'))}
              placeholder="请选择到期日期"
            />
          </Form.Item>
          <Form.Item name="reason" label="操作原因" rules={[{ required: true, message: '请输入操作原因' }]}>
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="说明新增或调整会员的原因，内容会记录到后台审计日志" />
          </Form.Item>
          <Typography.Paragraph type="secondary" className="membership-editor-form__tip">
            {opensTeamMembership
              ? '该团队当前未开通会员；选择套餐并保存后将正式开通，且不会自动修改团队积分余额。'
              : '后台会员操作不会创建虚假支付订单，也不会自动修改个人或团队积分余额。'}
          </Typography.Paragraph>
        </Form>
      </Modal>
    </>
  );
}
